import type { UserType } from "@/types"

/**
 * Fuente única de verdad de la jerarquía de usuarios del OFV.
 *
 * Dos ejes ortogonales (ver docs/JERARQUIA_USUARIOS_Y_PERFILES.md):
 *  - `tipo_usuario` (la capa): ADMIN → A → {M, B, R} → C. Inmutable.
 *  - "Perfil": comisión (cuánto gana) vs permisos (qué ve). No es lo mismo
 *    que el tipo.
 *
 * El `tipo` interno (la letra) NO cambia en DB/API. Aquí solo viven las
 * etiquetas de NEGOCIO que ve el usuario final y la matriz de creación,
 * espejo de `TIPO_PERMITIDO_POR_CREADOR` en
 * api/services/usuario_service.py.
 */

/** Etiquetas de negocio. Sin letras visibles para el usuario final. */
export const TIPO_LABELS: Record<UserType, string> = {
  Admin: "Administrador",
  A: "Marca blanca",
  M: "Mayorista (Master)",
  B: "Distribuidor",
  C: "Punto de venta",
  R: "Punto de recarga",
}

/**
 * Qué tipos REALES puede crear cada tipo. El CONJUNTO es espejo del backend
 * (`TIPO_PERMITIDO_POR_CREADOR`); el ORDEN es solo de UI (primero el más
 * común, que será el preseleccionado). No incluye la subcuenta/empleado:
 * ese es un valor virtual de la UI (mismo tipo que el creador, marca
 * `es_subcuenta=true`).
 */
export const CREATION_MATRIX: Partial<Record<UserType, UserType[]>> = {
  Admin: ["A", "Admin"],
  A: ["B", "M", "R"],
  B: ["C", "R"],
  // M, C, R: no crean cuentas (los B de un M los da de alta su A). El C sí da
  // de alta EMPLEADOS: ver `TIPOS_CON_EMPLEADOS`.
}

/** Etiqueta de negocio para un tipo (fallback al valor crudo si desconocido). */
export function tipoLabel(tipo?: string | null): string {
  if (!tipo) return ""
  return TIPO_LABELS[tipo as UserType] ?? tipo
}

/**
 * Descripción de NEGOCIO de cada capa, para mostrar bajo el selector de tipo
 * al crear una cuenta. Aclara qué hace cada nivel — en particular la diferencia
 * entre Punto de venta (C) y Punto de recarga (R), que se confunden.
 */
export const TIPO_DESCRIPCION: Record<UserType, string> = {
  Admin: "Administrador del sistema.",
  A: "Opera bajo tu marca. Puede crear Mayoristas, Distribuidores y Puntos de recarga, y define sus comisiones.",
  M: "Agrupa a varios Distribuidores y gana un override sobre sus ventas. No vende directo ni maneja inventario propio.",
  B: "Vende, activa líneas y maneja inventario de SIMs. Puede crear sus propios Puntos de venta y Puntos de recarga.",
  C: "Vende y activa líneas: portabilidad, activación, inventario de SIMs y recargas. Gana comisión por cada operación.",
  R: "Solo recargas de tiempo aire. NO maneja SIMs, NO hace portabilidad ni activación. Gana por el margen de la recarga, no por comisión configurable.",
}

/** Descripción de negocio de un tipo (cadena vacía si desconocido). */
export function descripcionCapa(tipo?: string | null): string {
  if (!tipo) return ""
  return TIPO_DESCRIPCION[tipo as UserType] ?? ""
}

/** Tipo preseleccionado al abrir "Nuevo usuario" (primer creable). */
export function defaultTipoForCreator(creator?: UserType | null): UserType | "" {
  const opts = creator ? CREATION_MATRIX[creator] : undefined
  return opts?.[0] ?? ""
}

/**
 * Quién tiene EMPLEADOS (subcuentas de su mismo tipo). Espejo de
 * `TIPOS_CON_EMPLEADOS` en api/services/usuario_service.py. El C los tiene
 * (desde 2026-10-05) aunque no cree cuentas; ADMIN crea las marcas, no
 * empleados; M y R no tienen.
 */
export const TIPOS_CON_EMPLEADOS: readonly UserType[] = ["A", "B", "C"]

/** ¿Este creador puede dar de alta empleados (subcuentas) de su mismo tipo? */
export function puedeCrearSubcuenta(
  creator?: UserType | null,
  esSubcuenta?: boolean,
): boolean {
  if (esSubcuenta || !creator) return false
  return TIPOS_CON_EMPLEADOS.includes(creator)
}

/** ¿Sólo crea empleados (no cuentas)? Es el caso del C. */
export function soloCreaEmpleados(creator?: UserType | null): boolean {
  return puedeCrearSubcuenta(creator, false) && !CREATION_MATRIX[creator!]?.length
}
